import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  ArrowRight,
  Share2,
  Shield,
  Wallet,
  BarChart2,
  Calculator,
  TrendingUp,
  Target,
  ChevronRight,
} from 'lucide-react';

const PRICE = 49;

// ─── Modelo de simulación ideal ───
// Fase inicial: 3 usuarios → 25% + 25% + 50% = 100% cuota
// Fase independiente: directos al 50%, red descendente al 25%
// La tabla ideal asume que cada usuario nuevo trae 2 más al mes siguiente

function simulateGrowth(months: number): { month: number; income: number; network: number; newUsers: number }[] {
  // Mes 1: 3 usuarios iniciales (25% + 25% + 50%) = 49€
  // Mes 2: el directo (3er usuario) trae 2 nuevos → indirectos al 25%
  // Mes 3+: cada grupo nuevo se duplica cada mes
  const results: { month: number; income: number; network: number; newUsers: number }[] = [];

  let income = PRICE; // mes 1: 49€
  let totalNetwork = 3;
  let newThisMonth = 2; // mes 2: 2 nuevos del directo

  results.push({ month: 1, income, network: totalNetwork, newUsers: 3 });

  for (let m = 2; m <= months; m++) {
    income += newThisMonth * PRICE * 0.25;
    totalNetwork += newThisMonth;
    results.push({ month: m, income, network: totalNetwork, newUsers: newThisMonth });
    newThisMonth *= 2;
  }

  return results;
}

// Cálculo simple de ingresos solo por directos (para el slider)
function calcDirectIncome(refs: number): number {
  if (refs <= 0) return 0;
  const first2 = Math.min(refs, 2) * PRICE * 0.25;
  const rest = Math.max(refs - 2, 0) * PRICE * 0.50;
  return first2 + rest;
}

const formatEur = (n: number): string =>
  n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const Ambassadors = () => {
  const [sliderValue, setSliderValue] = useState(5);
  const [showFullTable, setShowFullTable] = useState(false);
  const growthData = simulateGrowth(12);

  return (
    <div className="min-h-screen bg-dark-base">
      {/* HERO */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-base via-dark-base to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary/10 rounded-2xl">
                <Wallet className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white">
              Haz que la herramienta se pague sola
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
              Usa Asistente Trading IA, comprueba su valor y compártela con otros traders.
              Las comisiones recurrentes cubren tu suscripción.
            </p>
            <div className="mt-6 flex items-center justify-center space-x-2">
              <Target className="h-5 w-5 text-primary" />
              <p className="text-primary font-semibold text-lg">
                Con 3 traders activos cubres el 100% de tu cuota mensual
              </p>
            </div>
            <div className="mt-10">
              <Link to="/register" className="btn-primary inline-flex items-center space-x-2 text-lg px-8 py-3">
                <span>Empezar ahora</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* CÓMO FUNCIONA — LAS 2 FASES */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-card to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Cómo funciona</h2>
              <p className="mt-4 text-gray-400">Dos fases simples hacia la independencia</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Trader */}
              <div className="card p-6 border-l-4 border-l-yellow-500">
                <div className="flex items-center space-x-2 mb-4">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-500/20 text-yellow-400 text-sm font-bold">1</span>
                  <h3 className="text-lg font-semibold text-white">Trader</h3>
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">nivel inicial</span>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  Al suscribirte empiezas como Trader. Invita a 2 traders
                  para cubrir parte de tu cuota y calificar a Trader Pro:
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center py-1.5 px-3 bg-white/5 rounded">
                    <span className="text-gray-300">1er trader invitado</span>
                    <span className="text-yellow-400 font-medium">25% → {formatEur(PRICE * 0.25)}€</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 px-3 bg-white/5 rounded">
                    <span className="text-gray-300">2º trader invitado</span>
                    <span className="text-yellow-400 font-medium">25% → {formatEur(PRICE * 0.25)}€</span>
                  </div>
                  <div className="flex justify-between items-center py-2 px-3 bg-yellow-500/10 rounded">
                    <span className="text-yellow-400 font-medium">Subtotal</span>
                    <span className="text-yellow-400 font-medium">{formatEur(PRICE * 0.50)}€/mes</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Con 2 referidos activos calificas automáticamente a Trader Pro.
                  Tus 2 primeros quedan en la red del Trader Pro que te invitó.
                </p>
              </div>

              {/* Trader Pro */}
              <div className="card p-6 border-l-4 border-l-green-500">
                <div className="flex items-center space-x-2 mb-4">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500/20 text-green-400 text-sm font-bold">2</span>
                  <h3 className="text-lg font-semibold text-white">Trader Pro</h3>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">red propia</span>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  Inicias tu propia red. Tus nuevos directos y toda
                  la red que se forme debajo genera comisiones para ti:
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center py-1.5 px-3 bg-white/5 rounded">
                    <span className="text-gray-300">Tus directos (3º en adelante)</span>
                    <span className="text-green-400 font-medium">50% → {formatEur(PRICE * 0.50)}€</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 px-3 bg-white/5 rounded">
                    <span className="text-gray-300">Red descendente</span>
                    <span className="text-yellow-400 font-medium">25% → {formatEur(PRICE * 0.25)}€</span>
                  </div>
                </div>
                <div className="mt-4 bg-primary/10 rounded p-3">
                  <p className="text-xs text-gray-300">
                    <strong className="text-primary">Ejemplo con 3 traders:</strong> tus 2 primeros al 25% ({formatEur(PRICE * 0.25 * 2)}€) + tu 3er directo al 50% ({formatEur(PRICE * 0.50)}€) = <strong className="text-primary">{formatEur(PRICE)}€/mes</strong> → suscripción cubierta al 100%
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  La calificación Trader Pro es permanente.
                  Si un referido inicial se desuscribe, pierdes esa comisión
                  pero mantienes tu nivel y tu red.
                </p>
              </div>
            </div>

            <p className="text-center text-sm text-gray-400 mt-8 max-w-2xl mx-auto">
              Tu suscripción de {formatEur(PRICE)}€/mes sigue activa siempre.
              Las comisiones compensan el coste. Con 3 traders activos tu herramienta se paga sola.
            </p>
          </div>
        </div>
      </div>

      {/* CALCULADOR DE DIRECTOS */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-base to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Calcula tus comisiones directas</h2>
              <p className="mt-4 text-gray-400">
                Solo por tus referidos directos, sin contar la red descendente
              </p>
            </div>

            <div className="card p-8 max-w-xl mx-auto mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Calculator className="h-6 w-6 text-primary" />
                <h3 className="text-white font-semibold">¿Cuántos traders invitarías?</h3>
              </div>
              <input
                type="range"
                min={1}
                max={25}
                value={sliderValue}
                onChange={(e) => setSliderValue(parseInt(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                {[1, 5, 10, 15, 20, 25].map(n => <span key={n}>{n}</span>)}
              </div>
              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm">
                  Con <span className="text-white font-bold text-lg">{sliderValue}</span> referidos directos activos
                </p>
                <p className="text-4xl font-extrabold text-green-400 mt-2">
                  {formatEur(calcDirectIncome(sliderValue))}€<span className="text-lg text-gray-400 font-normal">/mes</span>
                </p>
                {sliderValue >= 3 && (
                  <p className="text-sm text-primary mt-2">
                    ✓ Suscripción cubierta + {formatEur(calcDirectIncome(sliderValue) - PRICE)}€ extra
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* POTENCIAL DE CRECIMIENTO — TABLA MES A MES */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-card to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-4">
                <TrendingUp className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Potencial de crecimiento</h2>
              <p className="mt-4 text-gray-400 max-w-xl mx-auto">
                Si cada usuario nuevo trae 2 traders al mes siguiente, así evoluciona tu red.
                Este es un escenario ideal de referencia.
              </p>
            </div>

            <div className="card p-6 max-w-2xl mx-auto">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="pb-3 text-left text-gray-400">Mes</th>
                      <th className="pb-3 text-right text-gray-400">Red total</th>
                      <th className="pb-3 text-right text-gray-400">Ingreso mensual</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {growthData.slice(0, showFullTable ? 12 : 6).map((d) => (
                      <tr key={d.month} className={`hover:bg-white/5 ${d.month === 1 ? 'bg-primary/5' : ''}`}>
                        <td className="py-3 text-white font-medium">
                          {MONTH_NAMES[d.month - 1]}
                          {d.month === 1 && <span className="text-xs text-primary ml-2">← cuota cubierta</span>}
                        </td>
                        <td className="py-3 text-right text-gray-300">{d.network} traders</td>
                        <td className="py-3 text-right text-green-400 font-semibold">{formatEur(d.income)}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!showFullTable && (
                <button
                  onClick={() => setShowFullTable(true)}
                  className="mt-4 w-full py-2 text-sm text-gray-400 hover:text-white bg-white/5 rounded-lg transition-colors flex items-center justify-center"
                >
                  Ver los 12 meses <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              )}

              <div className="mt-4 bg-amber-900/20 border border-amber-700/30 rounded-lg p-3">
                <p className="text-xs text-amber-300/80 text-center">
                  ⚠️ Escenario de potencial ideal. No es una promesa de ingresos.
                  Los resultados dependen de cuántos usuarios mantengan su suscripción activa.
                  Tu suscripción de {formatEur(PRICE)}€/mes sigue siendo un pago real.
                  *Los porcentajes se calculan sobre el importe neto de la suscripcion, excluidos impuestos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* POR QUÉ TIENE SENTIDO */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-base to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Por qué tiene sentido</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <BenefitCard
                icon={<Shield className="h-7 w-7 text-blue-400" />}
                title="Producto real, no humo"
                desc="Recomiendas una herramienta de análisis validada con 11 años de datos y métricas públicas. No es un curso ni señales."
              />
              <BenefitCard
                icon={<Wallet className="h-7 w-7 text-green-400" />}
                title="Cubre tus costes de trading"
                desc="Usa las comisiones para pagar cuentas de fondeo, datos de mercado o cualquier gasto de tu operativa."
              />
              <BenefitCard
                icon={<TrendingUp className="h-7 w-7 text-yellow-400" />}
                title="Ingresos que crecen contigo"
                desc="Tu red genera comisiones recurrentes cada mes. Cuanto más valor aportas a otros traders, más crece."
              />
              <BenefitCard
                icon={<BarChart2 className="h-7 w-7 text-primary" />}
                title="Control total"
                desc="Ve tus referidos, comisiones y estado de cada uno en tiempo real desde tu panel de control."
              />
            </div>
          </div>
        </div>
      </div>

      {/* CTA FINAL */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-base to-dark-overlay"></div>
        <div className="relative py-20 z-10">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Que tu herramienta de trading se pague sola
            </h2>
            <p className="mt-4 text-lg text-gray-300">
              Suscríbete, comprueba el valor de la herramienta,
              compártela con otros traders y cubre tu suscripción.
            </p>
            <p className="mt-2 text-sm text-gray-500">
              El programa de embajadores está disponible para suscriptores activos.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/register" className="btn-primary inline-flex items-center justify-center space-x-2 text-lg px-8 py-3">
                <span>Empezar ahora</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/login" className="btn-secondary inline-flex items-center justify-center text-lg px-8 py-3">
                Ya tengo cuenta
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BenefitCard: React.FC<{ icon: React.ReactNode; title: string; desc: string }> = ({ icon, title, desc }) => (
  <div className="card p-6 hover:border-primary/20 transition-all duration-300">
    <div className="flex items-start space-x-4">
      <div className="p-2 bg-white/5 rounded-lg shrink-0">{icon}</div>
      <div>
        <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
        <p className="text-sm text-gray-400">{desc}</p>
      </div>
    </div>
  </div>
);

export default Ambassadors;
